import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function TaxPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Tax Summary';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const [year, setYear]       = useState(new Date().getFullYear());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/payments/tax/summary?year=${year}`)
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load tax summary'))
      .finally(() => setLoading(false));
  }, [year]);

  function exportCSV() {
    if (!data) return;
    const rows = [
      ['Type', 'Date', 'Satoshis', 'Cost Basis', 'Proceeds', 'Gain/Loss', 'Term', 'Days Held'],
      ...(data.events || []).map(e => [
        'BTC Sale',
        e.date || '',
        e.satoshis || '',
        e.costBasis || '',
        e.proceeds || '',
        e.gainLoss || '',
        e.taxTreatment === 'long_term' ? 'Long-term' : 'Short-term',
        e.holdingDays || ''
      ]),
      [],
      ['Summary', '', '', '', '', '', '', ''],
      ['Short-term gains', '', '', '', '', data.summary.shortTermGainLoss, 'Short-term', ''],
      ['Long-term gains', '', '', '', '', data.summary.longTermGainLoss, 'Long-term', ''],
      ['Total gains', '', '', '', '', data.summary.totalGainLoss, '', ''],
    ];
    const csv  = rows.map(r => r.join(',')).join(String.fromCharCode(10));
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'thunderbolt-tax-' + year + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const years = [2026, 2025, 2024];

  return (
    <div style={s.page}>

      {/* Header */}
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Tax Summary</span>
        <button style={s.exportBtn} onClick={exportCSV} disabled={!data}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{marginRight:5, verticalAlign:'middle'}}>
            <path d="M7.5 1v8M4.5 6.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M1.5 11.5v1a1 1 0 001 1h10a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          CSV
        </button>
      </div>

      <div style={s.body}>

        {/* Year selector */}
        <div style={s.yearRow}>
          {years.map(y => (
            <button key={y}
              style={{...s.yearBtn, ...(year===y ? s.yearBtnActive : {})}}
              onClick={() => setYear(y)}>
              {y}
            </button>
          ))}
        </div>

        {/* Disclaimer */}
        /*<div style={s.disclaimer}>
          <div style={s.disclaimerTitle}>Tax disclaimer</div>
          <div style={s.disclaimerText}>
            This summary is for informational purposes only and does not constitute tax advice.
            Consult a qualified tax professional. Cashing out Bitcoin is a taxable event in most jurisdictions.
          </div>
        </div>*/

        {loading ? (
          <div style={s.loading}>Loading...</div>
        ) : error ? (
          <div style={s.errorCard}>{error}</div>
        ) : data ? (
          <>
            {/* Summary cards */}
            <div style={s.summaryGrid}>
              <div style={s.summaryCard}>
                <div style={s.summaryLabel}>Short-term gains</div>
                <div style={{...s.summaryValue,
                  color: parseFloat(data.summary.shortTermGainLoss) >= 0 ? '#00C48C' : '#FF3B30'}}>
                  {parseFloat(data.summary.shortTermGainLoss) >= 0 ? '+' : ''}
                  ${data.summary.shortTermGainLoss}
                </div>
                <div style={s.summarySubLabel}>Held under 1 year</div>
                <div style={s.summarySubLabel}>Taxed as ordinary income</div>
              </div>
              <div style={s.summaryCard}>
                <div style={s.summaryLabel}>Long-term gains</div>
                <div style={{...s.summaryValue,
                  color: parseFloat(data.summary.longTermGainLoss) >= 0 ? '#00C48C' : '#FF3B30'}}>
                  {parseFloat(data.summary.longTermGainLoss) >= 0 ? '+' : ''}
                  ${data.summary.longTermGainLoss}
                </div>
                <div style={s.summarySubLabel}>Held over 1 year</div>
                <div style={s.summarySubLabel}>Taxed at 0-20%</div>
              </div>
            </div>

            <div style={s.totalCard}>
              <div style={s.totalLabel}>Total gain / loss for {year}</div>
              <div style={{...s.totalValue,
                color: parseFloat(data.summary.totalGainLoss) >= 0 ? '#00C48C' : '#FF3B30'}}>
                {parseFloat(data.summary.totalGainLoss) >= 0 ? '+' : ''}
                ${data.summary.totalGainLoss}
              </div>
              {data.summary.needsReporting ? (
                <div style={s.reportingBadge}>Reportable on tax return</div>
              ) : (
                <div style={s.noReportingBadge}>No reportable events</div>
              )}
            </div>

            {/* Events table */}
            {data.events.length === 0 ? (
              <div style={s.emptyCard}>
                <div style={s.emptyIconWrap}>
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                    <rect x="6" y="4" width="24" height="28" rx="4" stroke="#AEAEB2" strokeWidth="1.5"/>
                    <path d="M12 12h12M12 18h12M12 24h8" stroke="#AEAEB2" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={s.emptyTitle}>No taxable events in {year}</div>
                <div style={s.emptySub}>
                  Bitcoin cashouts will appear here. Holding BTC is not a taxable event.
                </div>
              </div>
            ) : (
              <div style={s.card}>
                <div style={s.cardTitle}>Taxable Events</div>
                {data.events.map((event, i) => (
                  <div key={i} style={{...s.eventRow,
                    borderBottom: i < data.events.length-1 ? '1px solid #F5F5F7' : 'none'}}>
                    <div style={s.eventLeft}>
                      <div style={s.eventDate}>{event.date}</div>
                      <div style={s.eventType}>BTC Sale</div>
                      <div style={s.eventSats}>{event.satoshis.toLocaleString()} sats</div>
                    </div>
                    <div style={s.eventMiddle}>
                      <div style={s.eventRow2}>
                        <span style={s.eventLabel}>Cost basis</span>
                        <span style={s.eventVal}>${event.costBasis}</span>
                      </div>
                      <div style={s.eventRow2}>
                        <span style={s.eventLabel}>Proceeds</span>
                        <span style={s.eventVal}>${event.proceeds}</span>
                      </div>
                      <div style={s.eventRow2}>
                        <span style={s.eventLabel}>BTC price</span>
                        <span style={s.eventVal}>${parseFloat(event.btcPrice).toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={s.eventRight}>
                      <div style={{...s.gainLoss,
                        color: parseFloat(event.gainLoss) >= 0 ? '#00C48C' : '#FF3B30'}}>
                        {parseFloat(event.gainLoss) >= 0 ? '+' : ''}${event.gainLoss}
                      </div>
                      <div style={{...s.taxTreatment,
                        color: event.taxTreatment === 'long_term' ? '#007AFF' : '#FF9500'}}>
                        {event.taxTreatment === 'long_term' ? 'Long-term' : 'Short-term'}
                      </div>
                      <div style={s.holdingDays}>{event.holdingDays}d held</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info box */}
            <div style={s.infoCard}>
              <div style={s.infoTitle}>How your Earning rewards work</div>
              {[
                'A percentage of each payment you receive is automatically converted to Bitcoin (BTC)',
                'Bitcoin is held in Thunderbolt\'s corporate treasury, with a share allocated to your Earning balance',
                'Your Earning balance shows the current USD value of your allocated Bitcoin',
                'Bitcoin\'s value fluctuates — your Earning balance will go up and down with the market',
              ].map((item, i) => (
                <div key={i} style={s.infoItem}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{flexShrink:0, marginTop:1}}>
                    <circle cx="7" cy="7" r="6" stroke="#007AFF" strokeWidth="1.2"/>
                    <path d="M4.5 7l2 2 3-3" stroke="#007AFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{item}</span>
                </div>
              ))}
              <div style={{...s.infoTitle, marginTop:14}}>Tax implications</div>
              {[
                'Receiving an Earning allocation is generally not a taxable event',
                'Cashing out your Earning balance IS generally a taxable event',
                'Short-term gains (under 1 year) taxed as ordinary income',
                'Long-term gains (over 1 year) taxed at 0%, 15%, or 20%',
                'Losses can offset gains — keep records of all transactions',
                'Thunderbolt uses FIFO (first in, first out) cost basis method',
                'Consult a tax professional for advice specific to your situation',
              ].map((item, i) => (
                <div key={i} style={s.infoItem}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{flexShrink:0, marginTop:1}}>
                    <circle cx="7" cy="7" r="6" stroke="#007AFF" strokeWidth="1.2"/>
                    <path d="M4.5 7l2 2 3-3" stroke="#007AFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Export note */}
            <div style={s.exportNote}>
              CSV export is compatible with TurboTax, CoinTracker, Koinly, and TaxBit
            </div>

          </>
        ) : null}
      </div>
    </div>
  );
}

const s = {
  page:            {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:          {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:            {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', cursor:'pointer'},
  headerTitle:     {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  exportBtn:       {display:'flex', alignItems:'center', padding:'8px 14px', background:'#007AFF', color:'#fff', border:'none', borderRadius:10, fontSize:13, fontWeight:600, cursor:'pointer', boxShadow:'0 2px 8px rgba(0,122,255,0.3)', opacity:1},
  body:            {padding:'0 16px'},
  yearRow:         {display:'flex', gap:8, marginBottom:16},
  yearBtn:         {flex:1, padding:'10px 0', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:10, fontSize:14, fontWeight:500, color:'#6E6E73', cursor:'pointer'},
  yearBtnActive:   {background:'#007AFF', borderColor:'#007AFF', color:'#fff'},
  disclaimer:      {background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 14px', marginBottom:16},
  disclaimerTitle: {fontSize:13, fontWeight:600, color:'#92400E', marginBottom:4},
  disclaimerText:  {fontSize:12, color:'#92400E', lineHeight:1.6},
  loading:         {textAlign:'center', padding:40, color:'#6E6E73'},
  errorCard:       {background:'#FFF0EF', borderRadius:12, padding:16, color:'#FF3B30', fontSize:14},
  summaryGrid:     {display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12},
  summaryCard:     {background:'#fff', borderRadius:16, padding:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  summaryLabel:    {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5},
  summaryValue:    {fontSize:22, fontWeight:700, marginBottom:4},
  summarySubLabel: {fontSize:11, color:'#6E6E73', lineHeight:1.5},
  totalCard:       {background:'#fff', borderRadius:16, padding:20, marginBottom:16, textAlign:'center', boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  totalLabel:      {fontSize:13, color:'#6E6E73', marginBottom:8},
  totalValue:      {fontSize:36, fontWeight:700, letterSpacing:-1, marginBottom:12},
  reportingBadge:  {display:'inline-block', background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:8, padding:'6px 12px', fontSize:12, color:'#B87A00', fontWeight:500},
  noReportingBadge:{display:'inline-block', background:'#E6FAF5', border:'1px solid #A7F3D0', borderRadius:8, padding:'6px 12px', fontSize:12, color:'#00A86B', fontWeight:500},
  emptyCard:       {background:'#fff', borderRadius:20, padding:40, textAlign:'center', marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  emptyIconWrap:   {display:'flex', justifyContent:'center', marginBottom:16},
  emptyTitle:      {fontSize:16, fontWeight:600, marginBottom:8},
  emptySub:        {fontSize:13, color:'#6E6E73', lineHeight:1.6},
  card:            {background:'#fff', borderRadius:16, padding:20, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  cardTitle:       {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:16, textTransform:'uppercase', letterSpacing:0.8},
  eventRow:        {display:'flex', gap:12, padding:'14px 0', alignItems:'flex-start'},
  eventLeft:       {width:80, flexShrink:0},
  eventDate:       {fontSize:12, fontWeight:600, color:'#1D1D1F', marginBottom:3},
  eventType:       {fontSize:11, color:'#6E6E73', marginBottom:3},
  eventSats:       {fontSize:10, color:'#AEAEB2'},
  eventMiddle:     {flex:1},
  eventRow2:       {display:'flex', justifyContent:'space-between', fontSize:12, padding:'2px 0'},
  eventLabel:      {color:'#6E6E73'},
  eventVal:        {fontWeight:500, color:'#1D1D1F'},
  eventRight:      {textAlign:'right', flexShrink:0},
  gainLoss:        {fontSize:16, fontWeight:700, marginBottom:4},
  taxTreatment:    {fontSize:11, fontWeight:600, marginBottom:2},
  holdingDays:     {fontSize:11, color:'#AEAEB2'},
  infoCard:        {background:'#EBF4FF', borderRadius:16, padding:16, marginBottom:16},
  infoTitle:       {fontSize:13, fontWeight:600, color:'#007AFF', marginBottom:10},
  infoItem:        {display:'flex', gap:8, fontSize:12, color:'#1D1D1F', marginBottom:6, lineHeight:1.5},
  exportNote:      {fontSize:11, color:'#AEAEB2', textAlign:'center', marginBottom:16},
};
